<template>
    <div class="support-message-wrapper" v-bind:class="{
        'message-sent': isSentMessage,
        'message-received': !isSentMessage
    }">
        <div class="message-bubble" v-bind:class="{
            'bubble-sent': isSentMessage,
            'bubble-received': !isSentMessage,
            'bubble-staff': message.sender.rank === 'admin' || message.sender.rank === 'mod'
        }">
            <div class="bubble-content">
                <div v-if="!isSentMessage" class="bubble-header">
                    <AvatarImage v-bind:image="message.sender.avatar" class="bubble-avatar" />
                    <div class="bubble-meta">
                        <span class="bubble-username">{{ message.sender.username }}</span>
                        <span v-if="message.sender.rank === 'admin' || message.sender.rank === 'mod'" class="bubble-badge">STAFF</span>
                    </div>
                </div>
                <div class="bubble-text" v-html="message.message"></div>
                <div class="bubble-footer">
                    <span v-if="message.pending" class="bubble-sending">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="2" stroke-opacity="0.3"/>
                            <path d="M6 1C8.76142 1 11 3.23858 11 6" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                                <animate attributeName="stroke-dasharray" values="0 15;10 15;0 15" dur="1s" repeatCount="indefinite"/>
                                <animate attributeName="stroke-dashoffset" values="0;-5;-15" dur="1s" repeatCount="indefinite"/>
                            </path>
                        </svg>
                        Sending...
                    </span>
                    <span v-else class="bubble-time">{{ formatTime(message.createdAt) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AvatarImage from '@/components/AvatarImage';

export default {
    name: 'SupportMessageElement',
    components: {
        AvatarImage
    },
    props: [
        'message'
    ],
    computed: {
        ...mapGetters([
            'authUser'
        ]),
        isSentMessage() {
            if (!this.authUser.user) return false;
            
            // Get sender user ID - handle different data structures
            let senderUserId = null;
            
            // Try multiple ways to get sender user ID
            if (this.message.sender?.user) {
                // sender.user might be an object with _id, or a string/ID directly
                if (typeof this.message.sender.user === 'object' && this.message.sender.user !== null) {
                    senderUserId = this.message.sender.user._id || this.message.sender.user.id || this.message.sender.user;
                } else {
                    senderUserId = this.message.sender.user;
                }
            } else if (this.message.sender?._id) {
                // Fallback to sender._id
                senderUserId = this.message.sender._id;
            }
            
            if (!senderUserId) {
                // Debug: log when we can't find sender ID
                console.log('[SupportMessageElement] Could not determine sender ID', {
                    sender: this.message.sender,
                    messageId: this.message._id
                });
                return false;
            }
            
            // Get current user ID
            const currentUserId = this.authUser.user._id;
            
            // Convert both to strings for comparison (handles ObjectId vs string)
            const isSent = String(senderUserId) === String(currentUserId);
            
            // Debug logging (remove after fixing)
            if (process.env.NODE_ENV === 'development') {
                console.log('[SupportMessageElement] Message alignment check', {
                    senderUserId: String(senderUserId),
                    currentUserId: String(currentUserId),
                    isSent: isSent,
                    sender: this.message.sender
                });
            }
            
            return isSent;
        }
    },
    methods: {
        formatTime(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return 'Just now';
            } else if (diffMins < 60) {
                return `${diffMins}m ago`;
            } else if (diffHours < 24) {
                return `${diffHours}h ago`;
            } else if (diffDays < 7) {
                return `${diffDays}d ago`;
            } else {
                return date.toLocaleDateString();
            }
        }
    }
}
</script>

<style scoped>
.support-message-wrapper {
    width: 100%;
    display: flex;
    margin-bottom: 12px;
    padding: 0 15px;
    animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.support-message-wrapper.message-sent {
    justify-content: flex-end;
}

.support-message-wrapper.message-received {
    justify-content: flex-start;
}

.message-bubble {
    max-width: 70%;
    min-width: 120px;
    position: relative;
    display: flex;
    flex-direction: column;
}

.bubble-sent {
    align-items: flex-end;
}

.bubble-received {
    align-items: flex-start;
}

.bubble-content {
    padding: 10px 14px;
    border-radius: 18px;
    position: relative;
    word-wrap: break-word;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.bubble-sent .bubble-content {
    background: linear-gradient(135deg, #01e1a4 0%, #00c896 100%);
    border-bottom-right-radius: 4px;
    color: #000000;
}

.bubble-received .bubble-content {
    background: rgba(255, 255, 255, 0.1);
    border-bottom-left-radius: 4px;
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.bubble-staff .bubble-content {
    background: linear-gradient(135deg, rgba(255, 204, 0, 0.2) 0%, rgba(255, 204, 0, 0.15) 100%);
    border: 1px solid rgba(255, 204, 0, 0.3);
    color: #ffffff;
}

.bubble-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.bubble-avatar {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
}

.bubble-avatar .avatar-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    overflow: hidden;
}

.bubble-staff .bubble-avatar .avatar-image {
    border-color: var(--accent-yellow);
    box-shadow: 0 0 8px rgba(255, 204, 0, 0.4);
}

.bubble-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.bubble-username {
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    opacity: 0.9;
}

.bubble-sent .bubble-username {
    color: rgba(0, 0, 0, 0.7);
}

.bubble-badge {
    font-size: 9px;
    font-weight: 700;
    color: var(--accent-yellow);
    padding: 2px 6px;
    background: rgba(255, 204, 0, 0.2);
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.bubble-text {
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
    color: inherit;
    margin-bottom: 4px;
}

.bubble-sent .bubble-text {
    color: #000000;
    font-weight: 500;
}

.bubble-received .bubble-text {
    color: #e0e0e0;
}

.bubble-staff .bubble-text {
    color: #ffffff;
}

.bubble-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 2px;
}

.bubble-time {
    font-size: 10px;
    opacity: 0.7;
    color: inherit;
    font-weight: 500;
}

.bubble-sent .bubble-time {
    color: rgba(0, 0, 0, 0.6);
}

.bubble-received .bubble-time {
    color: rgba(255, 255, 255, 0.6);
}

.bubble-staff .bubble-time {
    color: rgba(255, 255, 255, 0.7);
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .message-bubble {
        max-width: 85%;
    }
    
    .bubble-content {
        padding: 8px 12px;
    }
    
    .bubble-text {
        font-size: 13px;
    }
}
</style>

